/**
 * Clustering algorithms for grouping artists by tag similarity.
 * Uses a k-means-like approach based on tag overlap between artists.
 */
import * as d3 from 'd3';

export const calculateTagOverlap = (tags1, tags2) => {
    if (!Array.isArray(tags1) || !Array.isArray(tags2)) return 0;
    const intersection = tags1.filter(tag => tags2.includes(tag));
    return intersection.length / Math.max(tags1.length, tags2.length);
};

const isValidNode = (node) => {
    return node && typeof node.id !== 'undefined' && node.id !== null;
};

/**
 * Assign nodes to clusters using tag similarity.
 * Uses k-means++ style initialization then iterative balancing.
 */
export const assignClusters = (nodes, numClusters = 2, mobile = false) => {
    if (!Array.isArray(nodes) || !nodes.length) return new Map();

    const validNodes = nodes.filter(isValidNode);
    if (validNodes.length < numClusters) numClusters = validNodes.length;

    if (mobile && numClusters > 3) {
        numClusters = 3;
    }

    const idealClusterSize = Math.floor(validNodes.length / numClusters);
    const clusterAssignments = new Map();

    const centers = [];
    const unassignedNodes = [...validNodes];

    const firstIndex = Math.floor(Math.random() * unassignedNodes.length);
    centers.push(unassignedNodes[firstIndex]);
    unassignedNodes.splice(firstIndex, 1);

    while (centers.length < numClusters && unassignedNodes.length) {
        let maxMinOverlap = Infinity;
        let nextCenter = null;
        let nextCenterIndex = -1;

        unassignedNodes.forEach((node, idx) => {
            const minOverlap = Math.min(...centers.map(center =>
                calculateTagOverlap(center.tags, node.tags)
            ));

            if (minOverlap < maxMinOverlap) {
                maxMinOverlap = minOverlap;
                nextCenter = node;
                nextCenterIndex = idx;
            }
        });

        if (nextCenter) {
            centers.push(nextCenter);
            unassignedNodes.splice(nextCenterIndex, 1);
        } else break;
    }

    const clusterNodes = new Map(centers.map((_, i) => [i, []]));

    [...unassignedNodes, ...centers].forEach(node => {
        const overlaps = centers.map((center, idx) => ({
            cluster: idx,
            overlap: calculateTagOverlap(center.tags, node.tags)
        }));

        overlaps.sort((a, b) => b.overlap - a.overlap);

        for (const { cluster } of overlaps) {
            const currentSize = clusterNodes.get(cluster).length;
            if (currentSize < idealClusterSize + 1) {
                clusterNodes.get(cluster).push(node);
                clusterAssignments.set(node.id, cluster);
                break;
            }
        }

        if (!clusterAssignments.has(node.id)) {
            const smallestCluster = Array.from(clusterNodes.entries())
                .sort((a, b) => a[1].length - b[1].length)[0][0];
            clusterNodes.get(smallestCluster).push(node);
            clusterAssignments.set(node.id, smallestCluster);
        }
    });

    let iterations = 0;
    const maxIterations = mobile ? 10 : 100;
    let balanced = false;

    while (!balanced && iterations < maxIterations) {
        balanced = true;
        iterations++;

        const clusterSizes = Array.from(clusterNodes.entries())
            .map(([cluster, nodes]) => ({
                cluster,
                size: nodes.length
            }))
            .sort((a, b) => b.size - a.size);

        const sizeDiff = clusterSizes[0].size - clusterSizes[clusterSizes.length - 1].size;

        if (sizeDiff > 1) {
            balanced = false;
            const largestCluster = clusterSizes[0].cluster;
            const smallestCluster = clusterSizes[clusterSizes.length - 1].cluster;

            const largestNodes = clusterNodes.get(largestCluster);
            const smallestCenter = centers[smallestCluster];

            let bestNode = null;
            let bestOverlap = -1;

            largestNodes.forEach(node => {
                const overlap = calculateTagOverlap(smallestCenter.tags, node.tags);
                if (overlap > bestOverlap) {
                    bestOverlap = overlap;
                    bestNode = node;
                }
            });

            if (bestNode) {
                clusterNodes.set(largestCluster,
                    clusterNodes.get(largestCluster).filter(n => n.id !== bestNode.id)
                );
                clusterNodes.get(smallestCluster).push(bestNode);
                clusterAssignments.set(bestNode.id, smallestCluster);
            }
        }
    }

    return clusterAssignments;
};

/**
 * Get the most distinctive tags for a cluster compared to all other nodes.
 */
export const getClusterTags = (clusterNodes, allNodes) => {
    if (!clusterNodes?.length) return "";

    const clusterTagCounts = {};
    clusterNodes.forEach(node => {
        if (!Array.isArray(node.tags)) return;
        node.tags.forEach(tag => {
            clusterTagCounts[tag] = (clusterTagCounts[tag] || 0) + 1;
        });
    });

    const otherTagCounts = {};
    allNodes.forEach(node => {
        if (!Array.isArray(node.tags) || clusterNodes.find(n => n.id === node.id)) return;
        node.tags.forEach(tag => {
            otherTagCounts[tag] = (otherTagCounts[tag] || 0) + 1;
        });
    });

    const thisClusterSize = clusterNodes.length;
    const otherClusterSize = allNodes.length - thisClusterSize;

    const tagScores = Object.entries(clusterTagCounts).map(([tag, count]) => {
        const thisFreq = count / thisClusterSize;
        const otherFreq = (otherTagCounts[tag] || 0) / otherClusterSize;
        const distinctiveness = thisFreq - otherFreq;
        return { tag, distinctiveness };
    });

    return tagScores
        .sort((a, b) => b.distinctiveness - a.distinctiveness)
        .slice(0, 2)
        .map(({ tag }) => tag)
        .join(", ");
};

/**
 * Create cluster groups with labels from a set of nodes.
 */
export const createClusterGroups = (nodes, allNodes, mobile = false) => {
    if (!Array.isArray(nodes) || !nodes.length) {
        return {
            clusters: new Map(),
            clusterGroups: new Map(),
            clusterLabels: new Map()
        };
    }

    const validNodes = nodes.filter(isValidNode);
    const numClusters = mobile ?
        Math.max(1, Math.floor(validNodes.length / 8)) :
        Math.floor(validNodes.length / 15) + 1;

    const clusters = assignClusters(validNodes, numClusters, mobile);
    const clusterGroups = new Map();
    const clusterLabels = new Map();

    validNodes.forEach(node => {
        const cluster = clusters.get(node.id);
        if (cluster === undefined) return;

        if (!clusterGroups.has(cluster)) {
            clusterGroups.set(cluster, []);
        }
        clusterGroups.get(cluster).push(node);
    });

    clusterGroups.forEach((groupNodes, cluster) => {
        const clusterLabel = getClusterTags(groupNodes, allNodes || nodes);
        clusterLabels.set(cluster, clusterLabel);
    });

    return { clusters, clusterGroups, clusterLabels };
};

/**
 * Generate a smooth hull path around cluster points.
 */
export const generateHullPath = (points, mobile = false) => {
    if (points.length < 3) return '';

    const padding = mobile ? 60 : 100;
    const paddedPoints = points.map((p, i) => ({
        x: p.x + Math.cos(i * (2 * Math.PI / points.length)) * padding,
        y: p.y + Math.sin(i * (2 * Math.PI / points.length)) * padding
    }));

    const centroid = {
        x: d3.mean(paddedPoints, d => d.x),
        y: d3.mean(paddedPoints, d => d.y)
    };

    paddedPoints.sort((a, b) => {
        return Math.atan2(a.y - centroid.y, a.x - centroid.x)
            - Math.atan2(b.y - centroid.y, b.x - centroid.x);
    });

    const lineGenerator = d3.line()
        .x(d => d.x)
        .y(d => d.y)
        .curve(d3.curveCatmullRomClosed.alpha(.01));

    return lineGenerator(paddedPoints);
};
